import { CSSObject } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import { useInputFieldStyles } from "components/DesignComponents/InputField";
import { MenuAnchorEl } from "shared";

const useStyles = () => {
    const {
        blue700,
        gray50,
        gray100,
        gray200,
        gray300,
        gray700,
        gray400,
        gray900,
        red700,
        red800,
        textDisabled
    } = colors;

    const { boxShadow } = useInputFieldStyles();

    const buttonTitleStyles: CSSObject = {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    };

    const inputStyles: CSSObject = {
        padding: "7px 12px",
        borderRadius: "0 6px 6px 0",

        "& .MuiOutlinedInput-notchedOutline": {
            border: "none"
        },

        "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
                boxShadow: "none"
            }
        }
    };

    const menuStyles: CSSObject = {
        "& .MuiMenu-paper": {
            marginTop: "1px"
        }
    };

    const errorTextStyles: CSSObject = {
        display: "flex",
        alignItems: "center",
        color: gray700,
        fontSize: "14px",
        fontWeight: "500",
        lineHeight: "20px",
        letterSpacing: "0.1px",
        margin: "6px 0 0",

        "& > svg": {
            fontSize: "16px",
            color: red700,
            alignSelf: "flex-start",
            margin: "2px 4px 0 0"
        }
    };

    const focusStyles = (isError?: boolean): CSSObject => ({
        "--borderColor": isError ? red700 : blue700,
        boxShadow: boxShadow(isError)
    });

    const borderColorOnHover = (isDisabled?: boolean, isError?: boolean) => {
        if (isDisabled) {
            return isError ? red700 : gray300;
        }

        return isError ? red800 : gray400;
    };

    const containerStyles = (
        anchorEl: MenuAnchorEl,
        isFullwidth?: boolean,
        isDisabled?: boolean,
        isError?: boolean
    ): CSSObject => ({
        "--borderColor": isError ? red700 : gray300,
        display: "flex",
        alignItems: "center",
        maxWidth: isFullwidth ? "unset" : "290px",
        boxSizing: "border-box",
        border: "1px solid var(--borderColor)",
        borderRadius: "6px",
        width: "100%",
        transition: "border-color 200ms ease",

        ...(anchorEl && focusStyles(isError)),

        "&:hover": {
            "--borderColor": borderColorOnHover(isDisabled, isError)
        },

        "&:focus-within": focusStyles(isError),

        "@media(max-width: 839px)": {
            maxWidth: "unset",
            flex: "1"
        }
    });

    const buttonStyles = (
        hasMenu: boolean,
        isDisabled?: boolean
    ): CSSObject => ({
        display: "flex",
        alignItems: "center",
        alignSelf: "normal",
        borderRight: "1px solid var(--borderColor)",
        borderRadius: "6px 0 0 6px",
        background: isDisabled ? gray100 : gray50,
        color: isDisabled ? textDisabled : gray900,
        fontSize: "14px",
        fontWeight: "500",
        lineHeight: "20px",
        letterSpacing: "0.1px",
        padding: hasMenu ? "0 6px 0 11px" : "0 11px",
        maxWidth: "128px",
        boxSizing: "border-box",
        cursor: !hasMenu || isDisabled ? "default" : "pointer",
        transition: "all 200ms ease",

        ...(hasMenu && {
            "&:hover": {
                background: isDisabled ? gray100 : gray200
            },

            "&:active": {
                background: isDisabled ? gray100 : gray300
            }
        })
    });

    return {
        buttonTitleStyles,
        inputStyles,
        menuStyles,
        errorTextStyles,
        containerStyles,
        buttonStyles
    };
};

export { useStyles };
